/**
 * React Query mutation hooks for QMS client ticket sub-resources
 * (line items, gateways, shipping, site visits)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  addLineItem,
  updateLineItem,
  deleteLineItem,
  addGateway,
  updateGateway,
  deleteGateway,
  upsertShipping,
  deleteShipping,
  addSiteVisit,
  updateSiteVisit,
  deleteSiteVisit,
} from '../api/qmsApi';
import type { QmsLineItem, QmsGateway, QmsShipping, QmsSiteVisit } from '../types/qms.types';
import { qmsKeys } from './useQmsBoard';

// --- Line Items ---

export function useAddLineItem(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<QmsLineItem>) => addLineItem(ticketId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(ticketId) });
    },
  });
}

export function useUpdateLineItem(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: Partial<QmsLineItem> }) =>
      updateLineItem(ticketId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(ticketId) });
    },
  });
}

export function useDeleteLineItem(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => deleteLineItem(ticketId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(ticketId) });
    },
  });
}

// --- Gateways ---

export function useAddGateway(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<QmsGateway>) => addGateway(ticketId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(ticketId) });
    },
  });
}

export function useUpdateGateway(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gatewayId, data }: { gatewayId: string; data: Partial<QmsGateway> }) =>
      updateGateway(ticketId, gatewayId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(ticketId) });
    },
  });
}

export function useDeleteGateway(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (gatewayId: string) => deleteGateway(ticketId, gatewayId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(ticketId) });
    },
  });
}

// --- Shipping ---

export function useUpsertShipping(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<QmsShipping>) => upsertShipping(ticketId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(ticketId) });
    },
  });
}

export function useDeleteShipping(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shippingId: string) => deleteShipping(ticketId, shippingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(ticketId) });
    },
  });
}

// --- Site Visits ---

export function useAddSiteVisit(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<QmsSiteVisit>) => addSiteVisit(ticketId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(ticketId) });
    },
  });
}

export function useUpdateSiteVisit(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ visitId, data }: { visitId: string; data: Partial<QmsSiteVisit> }) =>
      updateSiteVisit(ticketId, visitId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(ticketId) });
    },
  });
}

export function useDeleteSiteVisit(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (visitId: string) => deleteSiteVisit(ticketId, visitId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(ticketId) });
    },
  });
}
